/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, MouseEvent, useCallback, useRef } from "react";
import { BEM } from "@adapptio/lib-components";
import { isFunction } from "@adapptio/lib-shared";
import {
	FC_ANCHOR,
	FC_DIRECTION,
	FC_POSITION,
	FC_SIZE,
	FloatingContainer,
	FloatingContainerContent,
	FloatingContainerHeader
} from "../FloatingContainer";
import { FadeInOut } from "../FadeInOut";
import { useBackdropClick } from "@adapptio/lib-editor-ui";

export enum FIELD_FLOATING_PANEL_STYLE {
	STICKY = "sticky",
	ARROW = "arrow"
}

export enum FIELD_FLOATING_PANEL_SCHEMA {
	/** Primary panel color */
	PRIMARY = "primary",
	/** Dropdown panel color */
	DROPDOWN = "dropdown"
}

export interface IFieldFloatingPanelProps {
	/** Element to align panel to */
	for: React.MutableRefObject<HTMLElement>;
	/** If the panel is opened */
	isOpened: boolean;
	/** Where to place panel relative to target element */
	anchor?: FC_ANCHOR;
	/** Direction of panel relative to target element */
	direction?: FC_DIRECTION;
	/** How far to place element from target */
	offset?: number;
	/** Size of floating container. Might be height or width, depends on "direction" value. */
	size?: FC_SIZE;
	/** How to place panel (if stick to target or display arrow pointing to it) */
	style?: FIELD_FLOATING_PANEL_STYLE;
	/** Color schema */
	schema?: FIELD_FLOATING_PANEL_SCHEMA;
	/** Custom class name */
	className?: string;
	/** Called when panel is closed */
	onClose: () => void;
	/** Text in header */
	title?: string;
}

export const FieldFloatingPanel: FC<IFieldFloatingPanelProps> = ({
	for: target,
	isOpened,
	anchor,
	direction,
	offset,
	style,
	schema,
	className,
	onClose,
	size,
	children,
	title
}) => {
	const panelRef = useRef();
	const preventClick = useCallback(
		(ev: MouseEvent) => {
			if (isOpened) {
				ev.stopPropagation();
			}
		},
		[ isOpened ]
	);

	useBackdropClick(
		(event) => {
			if (isOpened && isFunction(onClose)) {
				event.preventDefault();
				event.stopPropagation();
				onClose();
			}
		},
		[ panelRef, target ],
		isOpened
	);

	return (
		<FadeInOut
			isVisible={isOpened}
			fadeOutDelay={250}
			render={(isVisible) => {
				const baseClass = BEM("edt-field-floating-panel", null, {
					"is-visible": isVisible,
					style: style || FIELD_FLOATING_PANEL_STYLE.ARROW,
					schema: schema || FIELD_FLOATING_PANEL_SCHEMA.PRIMARY
				});

				if (className) {
					baseClass.add(className);
				}

				return (
					<FloatingContainer
						containerRef={panelRef}
						parent={target}
						anchor={anchor || FC_ANCHOR.LEFT_CENTER}
						direction={direction || FC_DIRECTION.VR}
						size={size}
						position={FC_POSITION.AUTO}
						className={baseClass.toClassName()}
						addArrow={true}
						offset={offset !== undefined ? offset : 0}
						safePadding={10}
						updateInterval={200}
						onClose={onClose}
					>
						<FloatingContainerHeader onClose={onClose}>{title}</FloatingContainerHeader>
						<FloatingContainerContent
							className={baseClass.element("content").toClassName()}
							onClick={preventClick}
						>
							{children}
						</FloatingContainerContent>
					</FloatingContainer>
				);
			}}
		/>
	);
};

FieldFloatingPanel.defaultProps = {
	size: FC_SIZE.AUTO
};
